import t from 'react-translate';
import { useCallback, useState } from 'react';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { css } from '@emotion/react';
import {
  standardSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import ViewCommunication from './view-communication';
import EditCommunication from './edit-communication';
import { CommunicationModalState, ModalMode } from '../index';
import { config } from '@config/pendo.config.json';

const MODAL_WIDTH = 960;

const modalStyles = css`
  .bs4-modal-dialog {
    align-items: unset !important;
    top: ${tripleSpacing}px !important;
    height: calc(100vh - ${tripleSpacing}px - ${standardSpacing}px) !important;
  }
  .bs4-modal-content {
    height: 100% !important;
  }
`;

export type CustomCommunicationModalProps = {
  modalState: CommunicationModalState;
  setModalState: (state: CommunicationModalState) => void;
  registerCloseHandler?: (handler: () => void) => void;
  reset?: () => void;
};

const CustomCommunicationModal = ({
  modalState,
  setModalState,
  reset,
}: CustomCommunicationModalProps) => {
  const { mode } = modalState;
  const translationBase =
    t.MENTORING_PROGRAMS.COMMUNICATIONS.COMMUNICATION_MODAL;
  const dataQATag = config.pendo.athena.mentorshipProgram.communications;
  const viewDataQATag = dataQATag.viewCommunicationModal.closeButton;
  const editDataQATag = dataQATag.editCommunicationModal.cancelButton;
  const [closeHandler, setCloseHandler] = useState<(() => void) | null>(null);

  const communicationModalTitles = {
    [ModalMode.ADD]: translationBase.ADD_MODE.TITLE(),
    [ModalMode.VIEW]: translationBase.VIEW_MODE.TITLE(),
    [ModalMode.EDIT]: translationBase.EDIT_MODE.TITLE(),
    [ModalMode.COPY]: translationBase.COPY_MODE.TITLE(),
  };

  const CommunicationModalBody =
    mode === ModalMode.VIEW ? ViewCommunication : EditCommunication;

  const handleModalClose = useCallback(() => {
    if (mode !== ModalMode.VIEW && closeHandler) {
      closeHandler();
    } else {
      setModalState({ mode: null, communicationId: null });
    }
  }, [mode, closeHandler, setModalState]);

  const registerCloseHandler = useCallback((handler: () => void) => {
    setCloseHandler(() => handler);
  }, []);

  if (!mode) {
    return null;
  }

  return (
    <NvModal
      modalStyles={modalStyles}
      width={MODAL_WIDTH}
      show={!!mode}
      onClose={handleModalClose}
      type={ModalType.FIXED}
      header={communicationModalTitles[mode]}
      headerClassName='text-xl font-weight-light black'
      enforceFocus={false}
      body={
        <CommunicationModalBody
          modalState={modalState}
          setModalState={setModalState}
          registerCloseHandler={mode !== ModalMode.VIEW ? registerCloseHandler : undefined}
          reset={reset}
        />
      }
      closeDataQa={mode === ModalMode.VIEW ? viewDataQATag : editDataQATag}
      bodyClassName={mode === ModalMode.VIEW ? '' : 'p-0'}
    />
  );
};

export default CustomCommunicationModal;
